import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSources, sourcesFor } from './sources_context';

const API_URL = process.env.REACT_APP_API_URL || '';

const T = {
  bgBase: '#080b11', surface1: '#0e131d', surface2: '#0c1118',
  surfaceInset: '#0a0e16', surfaceRaised: '#0b0f17',
  border1: '#1a2230', border2: '#1e2735', borderWarm: '#33301d',
  textHi: '#e7ecf3', textBright: '#fff', textMid: '#c5cdd9',
  textMid2: '#9aa4b2', textMuted: '#8b94a3', textMuted2: '#7f8a9a',
  textFaint: '#6b7585',
  gold: '#fbbf24', green: '#34d399', red: '#f87171',
  indigo: '#818cf8', indigoLight: '#a5b4fc',
  blue: '#7fb0ff', cyan: '#5fd6df', orange: '#f0a35e', purple: '#c9a3ff',
  mono: "'JetBrains Mono', ui-monospace, monospace",
};

const BADGE_PALETTE = {
  blue: ['rgba(88,166,255,.14)', '#7cb7ff', 'rgba(88,166,255,.3)'],
  orange: ['rgba(240,136,62,.14)', '#f0a35e', 'rgba(240,136,62,.3)'],
  cyan: ['rgba(86,212,221,.13)', '#5fd6df', 'rgba(86,212,221,.3)'],
  green: ['rgba(52,211,153,.13)', '#46d39c', 'rgba(52,211,153,.3)'],
  red: ['rgba(248,113,113,.13)', '#f48b8b', 'rgba(248,113,113,.3)'],
  purple: ['rgba(188,140,255,.14)', '#c9a3ff', 'rgba(188,140,255,.3)'],
  amber: ['rgba(251,191,36,.14)', '#fbcf5b', 'rgba(251,191,36,.32)'],
  gray: ['rgba(139,148,163,.12)', '#9aa4b2', 'rgba(139,148,163,.25)'],
};
function Badge({ icon, label, hue = 'gray', title }) {
  const [bg, fg, bd] = BADGE_PALETTE[hue] || BADGE_PALETTE.gray;
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 8px', borderRadius: 7, background: bg, color: fg,
      border: `1px solid ${bd}`, fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      <span style={{ fontSize: 12 }}>{icon}</span>{label}
    </span>
  );
}

function CrossSourceIcons({ flags, style = {} }) {
  const items = [
    { icon: '📊', name: 'Pre-Market' },
    { icon: '🐦', name: 'Reddit/WSB' },
    { icon: '💬', name: 'StockTwits' },
    { icon: '🔥', name: 'Trending' },
  ];
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, ...style }}>
      {items.map((it, i) => (
        <span key={i} title={`${it.name}: ${flags[i] ? 'flagged' : 'not flagged'}`}
          style={{ fontSize: 13, opacity: flags[i] ? 1 : 0.2, filter: flags[i] ? 'none' : 'grayscale(1)' }}>{it.icon}</span>
      ))}
    </div>
  );
}

function StatCard({ label, icon, value, unit, sub, color = T.textHi, bd = T.border2 }) {
  return (
    <div style={{
      background: T.surface1, border: `1px solid ${bd}`, borderRadius: 16, padding: 18,
      display: 'flex', flexDirection: 'column', gap: 8,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.textMuted2, letterSpacing: '.06em', textTransform: 'uppercase' }}>{label}</span>
        <span style={{ fontSize: 15 }}>{icon}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontFamily: T.mono, fontSize: 27, fontWeight: 700, color, letterSpacing: '-.02em' }}>{value}</span>
        {unit && <span style={{ color: T.textMuted2, fontSize: 14, fontWeight: 600 }}>{unit}</span>}
      </div>
      <span style={{ color: T.textMuted2, fontSize: 11.5 }}>{sub}</span>
    </div>
  );
}

// Status pill component
function StatusPill({ status }) {
  const map = {
    OPEN:        { label: '● Open',       fg: T.blue,  bg: 'rgba(127,176,255,.13)', bd: 'rgba(127,176,255,.28)' },
    CLOSED:      { label: '◌ Closed',     fg: T.textMid2, bg: 'rgba(139,148,163,.12)', bd: 'rgba(139,148,163,.25)' },
    TARGET_HIT:  { label: '✓ Target hit', fg: T.green, bg: 'rgba(52,211,153,.13)', bd: 'rgba(52,211,153,.3)' },
    STOPPED:     { label: '✕ Stopped',    fg: T.red,   bg: 'rgba(248,113,113,.13)', bd: 'rgba(248,113,113,.3)' },
  };
  const m = map[status] || map.CLOSED;
  return (
    <span style={{
      padding: '3px 9px', borderRadius: 6, background: m.bg, color: m.fg,
      border: `1px solid ${m.bd}`, fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{m.label}</span>
  );
}

function StrategyChip({ strategy }) {
  // Classify into Swing or Day
  const isDay = /SHORT|MOMENTUM|STRADDLE|SPREAD/.test(strategy || '');
  const isPut = /PUT/.test(strategy || '');
  const c = isPut
    ? { bg: 'rgba(248,113,113,.13)', fg: T.red, label: 'Put' }
    : isDay
      ? { bg: 'rgba(240,136,62,.13)', fg: T.orange, label: 'Day' }
      : { bg: 'rgba(165,180,252,.13)', fg: T.indigoLight, label: 'Swing' };
  return (
    <span style={{
      padding: '3px 8px', borderRadius: 6, background: c.bg, color: c.fg,
      fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em',
    }}>{c.label} · {(strategy || '').replace('BUY_', '').replace('_', ' ')}</span>
  );
}

// Bullish/bearish split bar
function SentimentBar({ bull, bear, w = 130 }) {
  const total = bull + bear;
  if (total === 0) {
    return <div style={{ width: w, height: 6, borderRadius: 3, background: T.surfaceInset }} />;
  }
  const bullPct = (bull / total) * 100;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: w, height: 6, borderRadius: 3, background: T.red, position: 'relative', overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: `${bullPct}%`,
          background: T.green, borderRadius: '3px 0 0 3px',
        }} />
      </div>
      <span style={{ fontFamily: T.mono, fontSize: 11, color: T.textMid }}>
        <span style={{ color: T.green }}>{bullPct.toFixed(0)}%</span> /
        <span style={{ color: T.red }}> {(100 - bullPct).toFixed(0)}%</span>
      </span>
    </div>
  );
}

// Confidence bar (Reddit)
function ConfidenceBar({ value, w = 130 }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <div style={{ width: w, height: 6, borderRadius: 3, background: T.surfaceInset, overflow: 'hidden' }}>
        <div style={{
          width: `${v}%`, height: '100%',
          background: v >= 80 ? T.green : v >= 60 ? T.gold : T.orange,
          borderRadius: 3,
        }} />
      </div>
      <span style={{ fontFamily: T.mono, fontSize: 11, color: T.textMid }}>{v.toFixed(0)}%</span>
    </div>
  );
}

// ── Main Social Sentiment Tab ──────────────────────────────────────────────
export default function SocialV2() {
  const [wsbData, setWsbData] = useState([]);
  const [buzzLatest, setBuzzLatest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [source, setSource] = useState('combined');
  const [status, setStatus] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [spinning, setSpinning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [wsbResp, buzzResp] = await Promise.allSettled([
        axios.get(`${API_URL}/api/wsb-portfolio`),
        axios.get(`${API_URL}/api/buzz-scans`),
      ]);
      if (wsbResp.status === 'fulfilled') setWsbData(wsbResp.value.data || []);
      if (buzzResp.status === 'fulfilled') setBuzzLatest(buzzResp.value.data?.latest || null);
      setErr(null);
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setSpinning(true);
    await load();
    setTimeout(() => setSpinning(false), 600);
  };

  // ── Source-specific data ─────────────────────────────────────────────────
  const sources = useSources();
  const reddit = useMemo(() => wsbData.filter((t) => (t.source || '').toLowerCase().includes('reddit') || t.source === 'Reddit/WSB' || !t.source).map((t) => ({ ...t, _cross: sourcesFor(sources, t.symbol) })), [wsbData, sources]);
  const stocktwits = useMemo(() => (buzzLatest?.top_buzz || []).map((t) => ({ ...t, _cross: sourcesFor(sources, t.symbol) })), [buzzLatest, sources]);

  // Cross-reference Reddit ↔ StockTwits for Combined view
  const combined = useMemo(() => {
    const byTicker = new Map();
    reddit.forEach((r) => {
      const sym = r.symbol;
      if (!byTicker.has(sym)) byTicker.set(sym, { symbol: sym, reddit: null, stocktwits: null });
      byTicker.get(sym).reddit = r;
    });
    stocktwits.forEach((s) => {
      const sym = s.symbol;
      if (!byTicker.has(sym)) byTicker.set(sym, { symbol: sym, reddit: null, stocktwits: null });
      byTicker.get(sym).stocktwits = s;
    });
    return Array.from(byTicker.values()).map((t) => {
      const cross = sourcesFor(sources, t.symbol);
      const sourceCount = cross.filter(Boolean).length;
      return { ...t, sources: cross, sourceCount };
    }).sort((a, b) => b.sourceCount - a.sourceCount);
  }, [reddit, stocktwits, sources]);

  // Consensus = 2+ sources
  const consensus = useMemo(() => combined.filter((t) => t.sourceCount >= 2), [combined]);

  // ── Status filtering ─────────────────────────────────────────────────────
  const filterStatus = (t) => {
    if (status === 'all') return true;
    const s = (t.status || '').toUpperCase();
    if (status === 'open') return s === 'OPEN';
    if (status === 'closed') return s === 'CLOSED';
    if (status === 'target') return s === 'TARGET_HIT' || (t.percent_gain || 0) >= 50;
    if (status === 'stopped') return s === 'STOPPED' || (t.percent_gain || 0) <= -30;
    return true;
  };

  const filteredReddit = useMemo(() => reddit.filter(filterStatus), [reddit, status]);
  const filteredStocktwits = stocktwits; // no status on buzz scans
  const filteredCombined = useMemo(() => combined.filter((t) => {
    if (status === 'all') return true;
    return t.reddit && filterStatus(t.reddit);
  }), [combined, status]);

  // ── Stat cards per source ────────────────────────────────────────────────
  const stats = useMemo(() => {
    if (source === 'reddit') {
      const open = reddit.filter((t) => t.status === 'OPEN').length;
      const wins = reddit.filter((t) => (t.percent_gain || 0) > 0).length;
      const total = reddit.length;
      const avgConf = total ? reddit.reduce((a, b) => a + (b.confidence_score || 0), 0) / total : 0;
      return [
        { label: 'WSB tickers tracked', icon: '🐦', value: total, unit: '', sub: 'across 44 tracked trades', color: T.orange },
        { label: 'Avg confidence', icon: '📊', value: avgConf.toFixed(0), unit: '%', sub: 'post-engagement score', color: T.gold },
        { label: 'Open trades', icon: '⏳', value: open, unit: '', sub: `of ${total} total`, color: T.blue },
        { label: 'Win rate', icon: '✅', value: total ? ((wins / total) * 100).toFixed(0) : '0', unit: '%', sub: `${wins} winners`, color: T.green },
      ];
    } else if (source === 'stocktwits') {
      const msgs = stocktwits.reduce((a, b) => a + (b.total_messages || 0), 0);
      const total = stocktwits.length;
      const avgBull = total ? stocktwits.reduce((a, b) => a + (b.bullish_pct || 0), 0) / total : 0;
      const mostActive = stocktwits.length ? [...stocktwits].sort((a, b) => (b.total_messages || 0) - (a.total_messages || 0))[0] : null;
      return [
        { label: 'Messages scanned', icon: '💬', value: (msgs / 1000).toFixed(1), unit: 'k', sub: `across ${total} tickers`, color: T.cyan },
        { label: 'Avg bullish %', icon: '📈', value: avgBull.toFixed(0), unit: '%', sub: 'per-ticker mean', color: T.green },
        { label: 'ST tickers tracked', icon: '💬', value: total, unit: '', sub: 'latest buzz scan', color: T.cyan },
        { label: 'Most active', icon: '🔥', value: mostActive ? mostActive.symbol : '—', unit: '', sub: mostActive ? `${mostActive.total_messages} msgs` : '', color: T.gold },
      ];
    }
    // Combined
    const multi = combined.filter((t) => t.sourceCount >= 2).length;
    const tripPlus = combined.filter((t) => t.sourceCount >= 3).length;
    const wsbWin = reddit.filter((t) => (t.percent_gain || 0) > 0).length;
    return [
      { label: 'Multi-source picks', icon: '🔗', value: multi, unit: '', sub: '2+ sources agree', color: T.purple, bd: 'rgba(188,140,255,.32)' },
      { label: 'Triple+ consensus', icon: '⭐', value: tripPlus, unit: '', sub: '3+ sources flag', color: T.gold, bd: T.borderWarm },
      { label: 'Tracked trades', icon: '📒', value: reddit.length, unit: '', sub: 'WSB portfolio', color: T.indigo },
      { label: 'Win rate', icon: '✅', value: reddit.length ? ((wsbWin / reddit.length) * 100).toFixed(0) : '0', unit: '%', sub: `${wsbWin} winners`, color: T.green },
    ];
  }, [source, reddit, stocktwits, combined]);

  // ── Helpers ──────────────────────────────────────────────────────────────
  const fmtPct = (v) => v == null ? '—' : `${v >= 0 ? '+' : ''}${(typeof v === 'string' ? parseFloat(v) : v).toFixed(1)}%`;

  if (err) return <div style={{ padding: 60, textAlign: 'center', color: T.red }}>{err}</div>;
  if (loading && !wsbData.length && !buzzLatest) return <div style={{ padding: 60, textAlign: 'center', color: T.textMuted }}>Loading social data…</div>;

  const sourceSpec = [
    { key: 'reddit', icon: '🐦', label: 'Reddit / WSB', color: T.orange },
    { key: 'stocktwits', icon: '💬', label: 'StockTwits', color: T.cyan },
    { key: 'combined', icon: '🔗', label: 'Combined', color: T.purple },
  ];
  const actionLabels = {
    reddit: 'Refresh WSB trades',
    stocktwits: 'Run buzz scan',
    combined: 'Rescan all sources',
  };

  return (
    <div style={{ animation: 'risein .35s ease' }}>
      {/* Title row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: '-.02em' }}>📱 Social Sentiment</h1>
            <span style={{
              padding: '4px 10px', borderRadius: 8,
              background: 'rgba(188,140,255,.12)', border: '1px solid rgba(188,140,255,.32)',
              color: T.purple, fontSize: 11, fontWeight: 800, letterSpacing: '.04em',
            }}>CROWD SIGNAL</span>
          </div>
          <p style={{ margin: '8px 0 0', color: T.textMuted, fontSize: 13 }}>
            {source === 'reddit' && 'WSB swing trades scraped from r/wallstreetbets DD + momentum threads'}
            {source === 'stocktwits' && (buzzLatest ? `Buzz scan · ${buzzLatest.timestamp?.slice(0, 19) || ''} · ${stocktwits.length} tickers` : 'StockTwits sentiment')}
            {source === 'combined' && `${combined.length} tickers · ${consensus.length} with 2+ source confirmation`}
          </p>
        </div>
        <button onClick={refresh} style={{
          padding: '9px 16px', borderRadius: 10, border: `1px solid ${T.border2}`,
          background: T.surface1, color: T.purple, fontSize: 13, fontWeight: 700,
          cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 7,
        }}>
          <span style={{ display: 'inline-block', animation: spinning ? 'spin .9s linear' : 'none' }}>🔄</span>
          {actionLabels[source]}
        </button>
      </div>

      {/* Source toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.textMuted2, letterSpacing: '.06em', textTransform: 'uppercase' }}>Signal source</span>
        <div style={{ display: 'inline-flex', padding: 3, borderRadius: 11, background: T.surface2, border: `1px solid ${T.border2}` }}>
          {sourceSpec.map((s) => (
            <button key={s.key} onClick={() => setSource(s.key)} style={{
              padding: '7px 14px', borderRadius: 8, border: 'none',
              background: source === s.key ? '#1d2735' : 'transparent',
              color: source === s.key ? T.textHi : T.textMuted2,
              fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ fontSize: 14 }}>{s.icon}</span>{s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 22 }}>
        {stats.map((s, i) => <StatCard key={i} {...s} />)}
      </div>

      {/* Consensus spotlight (Combined only) */}
      {source === 'combined' && consensus.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.gold, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 12 }}>
            ⭐ Flagged by 2+ independent sources
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {consensus.slice(0, 3).map((t, i) => {
              const r = t.reddit;
              const s = t.stocktwits;
              const pnl = r?.percent_gain ?? 0;
              return (
                <div key={t.symbol + i} style={{
                  background: 'linear-gradient(160deg,#13110a,#0d1119)',
                  border: `1px solid ${T.borderWarm}`, borderRadius: 16, padding: 18,
                  boxShadow: '0 0 0 1px rgba(251,191,36,.25), 0 0 26px -8px rgba(251,191,36,.3)',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                    <span style={{ fontFamily: T.mono, fontSize: 22, fontWeight: 700, color: T.textBright }}>{t.symbol}</span>
                    {r && <StrategyChip strategy={r.strategy} />}
                    <span style={{ marginLeft: 'auto', fontFamily: T.mono, fontSize: 11, fontWeight: 700, color: T.gold, padding: '3px 9px', borderRadius: 5, background: 'rgba(251,191,36,.13)', border: '1px solid rgba(251,191,36,.32)' }}>
                      {t.sourceCount}/4 sources
                    </span>
                  </div>
                  <CrossSourceIcons flags={t.sources} style={{ marginBottom: 10 }} />
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 10 }}>
                    {r && (
                      <div>
                        <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>P&L</div>
                        <div style={{ fontFamily: T.mono, fontSize: 18, fontWeight: 700, color: pnl >= 0 ? T.green : T.red, marginTop: 2 }}>
                          {pnl >= 0 ? '+' : ''}{pnl.toFixed(0)}%
                        </div>
                      </div>
                    )}
                    {s && (
                      <div>
                        <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>Sentiment</div>
                        <div style={{ fontFamily: T.mono, fontSize: 14, fontWeight: 600, color: T.green, marginTop: 2 }}>
                          {(s.bullish_pct || 0).toFixed(0)}% bull
                        </div>
                      </div>
                    )}
                    <div style={{ marginLeft: 'auto' }}>
                      {r && <StatusPill status={r.status} />}
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {r?.pattern && r.pattern.split(',').slice(0, 3).map((p, j) => (
                      <Badge key={j} icon="🐦" label={p.trim()} hue="orange" />
                    ))}
                    {s?.has_options && <Badge icon="🎯" label="options" hue="purple" />}
                    {s?.catalyst_mentions > 0 && <Badge icon="⚡" label={`${s.catalyst_mentions} catalyst`} hue="amber" />}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Status filter chips */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: T.textMuted2, letterSpacing: '.06em', textTransform: 'uppercase' }}>Status</span>
        {[
          { k: 'all', l: 'All' },
          { k: 'open', l: 'Open' },
          { k: 'target', l: 'Target hit' },
          { k: 'stopped', l: 'Stopped' },
          { k: 'closed', l: 'Closed' },
        ].map((c) => (
          <button key={c.k} onClick={() => setStatus(c.k)} style={{
            padding: '5px 11px', borderRadius: 7, border: `1px solid ${status === c.k ? T.indigo : T.border2}`,
            background: status === c.k ? 'rgba(129,140,248,.13)' : T.surface2,
            color: status === c.k ? T.indigoLight : T.textMuted2,
            fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
          }}>{c.l}</button>
        ))}
      </div>

      {/* Ledger table */}
      <div style={{ background: T.surface1, border: `1px solid ${T.border2}`, borderRadius: 16, overflow: 'hidden' }}>
        {source === 'reddit' && (
          <RedditTable rows={filteredReddit} fmtPct={fmtPct} />
        )}
        {source === 'stocktwits' && (
          <StocktwitsTable rows={filteredStocktwits} fmtPct={fmtPct} />
        )}
        {source === 'combined' && (
          <CombinedTable rows={filteredCombined} expanded={expanded} setExpanded={setExpanded} fmtPct={fmtPct} />
        )}
      </div>
    </div>
  );
}

// ── Reddit ledger table ────────────────────────────────────────────────────
function RedditTable({ rows, fmtPct }) {
  if (rows.length === 0) return <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>No Reddit trades match this filter.</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: 880 }}>
        <Header cols={['Symbol', 'Mentions', 'Bullish confidence', 'Strategy', 'Entry', 'Status', 'P&L']} widths="100px 90px 1fr 130px 100px 110px 90px" />
        {rows.map((t, i) => {
          const pnl = parseFloat(t.percent_gain || 0);
          return (
            <div key={t.id || (t.symbol + i)} style={{
              display: 'grid', gridTemplateColumns: '100px 90px 1fr 130px 100px 110px 90px',
              gap: 12, padding: '11px 18px', borderBottom: `1px solid ${T.surface2}`, alignItems: 'center', fontSize: 12.5,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: T.mono, fontWeight: 700, color: T.blue }}>{t.symbol}</span>
                <CrossSourceIcons flags={t._cross || [false, true, false, false]} />
              </div>
              <div style={{ textAlign: 'right', fontFamily: T.mono, color: T.textMid }}>{t.mentions || t.post_score || '—'}</div>
              <ConfidenceBar value={t.confidence_score} />
              <div><StrategyChip strategy={t.strategy} /></div>
              <div style={{ fontFamily: T.mono, color: T.textHi }}>${(t.entry_price || 0).toFixed(2)}</div>
              <div><StatusPill status={t.status} /></div>
              <div style={{ textAlign: 'right', fontFamily: T.mono, color: pnl >= 0 ? T.green : T.red, fontWeight: 600 }}>
                {fmtPct(pnl)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── StockTwits ledger table ────────────────────────────────────────────────
function StocktwitsTable({ rows, fmtPct }) {
  if (rows.length === 0) return <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>No StockTwits buzz data.</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: 920 }}>
        <Header cols={['Symbol', 'Messages', 'Bullish vs Bearish', 'Strategy', 'Entry', 'Conf.', 'Day %']} widths="100px 90px 1fr 130px 80px 90px 90px" />
        {rows.map((t, i) => {
          const change = parseFloat(t.change_pct || 0);
          return (
            <div key={t.symbol + i} style={{
              display: 'grid', gridTemplateColumns: '100px 90px 1fr 130px 80px 90px 90px',
              gap: 12, padding: '11px 18px', borderBottom: `1px solid ${T.surface2}`, alignItems: 'center', fontSize: 12.5,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontFamily: T.mono, fontWeight: 700, color: T.blue }}>{t.symbol}</span>
                <CrossSourceIcons flags={t._cross || [false, false, true, false]} />
              </div>
              <div style={{ textAlign: 'right', fontFamily: T.mono, color: T.textMid }}>{t.total_messages || 0}</div>
              <SentimentBar bull={t.bullish || 0} bear={t.bearish || 0} />
              <div><StrategyChip strategy={t.strategy} /></div>
              <div style={{ fontFamily: T.mono, color: T.textHi }}>${(t.price || 0).toFixed(2)}</div>
              <div style={{ fontFamily: T.mono, color: t.confidence === 'HIGH' ? T.green : t.confidence === 'MEDIUM' ? T.gold : T.textMuted2, fontSize: 11, fontWeight: 700 }}>
                {t.confidence || '—'}
              </div>
              <div style={{ textAlign: 'right', fontFamily: T.mono, color: change >= 0 ? T.green : T.red, fontWeight: 600 }}>
                {fmtPct(change)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Combined ledger table ──────────────────────────────────────────────────
function CombinedTable({ rows, expanded, setExpanded, fmtPct }) {
  if (rows.length === 0) return <div style={{ padding: 40, textAlign: 'center', color: T.textMuted }}>No tickers in combined view.</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <div style={{ minWidth: 980 }}>
        <Header cols={['Symbol', 'Sources', 'Sentiment', 'Strategy', 'Entry', 'Status', 'P&L', '']} widths="100px 130px 1fr 130px 100px 110px 90px 30px" />
        {rows.map((t, i) => {
          const r = t.reddit;
          const s = t.stocktwits;
          const pnl = r ? parseFloat(r.percent_gain || 0) : null;
          const isExp = expanded[i];
          return (
            <React.Fragment key={t.symbol + i}>
              <div
                onClick={() => setExpanded((e) => ({ ...e, [i]: !e[i] }))}
                style={{
                  display: 'grid', gridTemplateColumns: '100px 130px 1fr 130px 100px 110px 90px 30px',
                  gap: 12, padding: '11px 18px',
                  borderBottom: `1px solid ${T.surface2}`,
                  background: t.sourceCount >= 3 ? 'rgba(251,191,36,.05)' : 'transparent',
                  cursor: 'pointer', alignItems: 'center', fontSize: 12.5,
                  transition: 'background .15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = T.surfaceInset; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = t.sourceCount >= 3 ? 'rgba(251,191,36,.05)' : 'transparent'; }}
              >
                <div style={{ fontFamily: T.mono, fontWeight: 700, color: T.blue }}>{t.symbol}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontFamily: T.mono, fontSize: 11, fontWeight: 700,
                    color: t.sourceCount >= 3 ? T.gold : T.blue,
                    padding: '2px 7px', borderRadius: 5,
                    background: t.sourceCount >= 3 ? 'rgba(251,191,36,.13)' : 'rgba(127,176,255,.13)',
                  }}>{t.sourceCount}/4</span>
                  <CrossSourceIcons flags={t.sources} />
                </div>
                <div>
                  {s ? <SentimentBar bull={s.bullish || 0} bear={s.bearish || 0} /> :
                    r ? <ConfidenceBar value={r.confidence_score} /> : <span style={{ color: T.textMuted, fontSize: 11 }}>—</span>}
                </div>
                <div>{r ? <StrategyChip strategy={r.strategy} /> : s ? <StrategyChip strategy={s.strategy} /> : '—'}</div>
                <div style={{ fontFamily: T.mono, color: T.textHi }}>${(r?.entry_price || s?.entry || s?.price || 0).toFixed(2)}</div>
                <div>{r ? <StatusPill status={r.status} /> : <span style={{ color: T.textMuted, fontSize: 11 }}>—</span>}</div>
                <div style={{ textAlign: 'right', fontFamily: T.mono, color: pnl == null ? T.textMuted : pnl >= 0 ? T.green : T.red, fontWeight: 600 }}>
                  {pnl == null ? '—' : fmtPct(pnl)}
                </div>
                <div style={{ textAlign: 'center', color: T.textMuted }}>{isExp ? '▲' : '▼'}</div>
              </div>
              {isExp && (
                <div style={{ padding: '14px 18px', background: T.surfaceInset, borderBottom: `1px solid ${T.surface2}` }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                    <div>
                      <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700, marginBottom: 6 }}>
                        🐦 Reddit / WSB
                      </div>
                      {r ? (
                        <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.7 }}>
                          <div>Strategy: <span style={{ color: T.textHi }}>{r.strategy}</span></div>
                          <div>Confidence: <span style={{ fontFamily: T.mono, color: T.textHi }}>{r.confidence_score}/100</span></div>
                          <div>Sentiment: <span style={{ color: T.textHi }}>{r.sentiment || r.rationale || '—'}</span></div>
                          {r.pattern && <div>Patterns: <span style={{ color: T.orange }}>{r.pattern}</span></div>}
                          {r.days_held != null && <div>Days held: <span style={{ fontFamily: T.mono, color: T.textHi }}>{r.days_held}</span></div>}
                          {r.strike && <div>Strike / expiry: <span style={{ fontFamily: T.mono, color: T.textHi }}>${r.strike} {r.expiry || ''}</span></div>}
                        </div>
                      ) : <div style={{ fontSize: 12, color: T.textMuted }}>Not flagged by Reddit/WSB.</div>}
                    </div>
                    <div>
                      <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700, marginBottom: 6 }}>
                        💬 StockTwits
                      </div>
                      {s ? (
                        <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.7 }}>
                          <div>Buzz score: <span style={{ fontFamily: T.mono, color: T.gold }}>{s.buzz_score}</span></div>
                          <div>Messages: <span style={{ fontFamily: T.mono, color: T.textHi }}>{s.total_messages}</span> ({s.bullish} bull / {s.bearish} bear)</div>
                          <div>Strategy: <span style={{ color: T.textHi }}>{s.strategy}</span> · <span style={{ color: s.confidence === 'HIGH' ? T.green : T.gold }}>{s.confidence}</span></div>
                          {s.rationale && <div>Rationale: <span style={{ color: T.textHi }}>{s.rationale}</span></div>}
                          {s.has_options && <div>Options: <span style={{ fontFamily: T.mono, color: T.purple }}>${s.strike} {s.expiry || ''}</span></div>}
                          <div>Day move: <span style={{ fontFamily: T.mono, color: (s.change_pct || 0) >= 0 ? T.green : T.red }}>{fmtPct(s.change_pct || 0)}</span></div>
                        </div>
                      ) : <div style={{ fontSize: 12, color: T.textMuted }}>Not flagged by StockTwits.</div>}
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function Header({ cols, widths }) {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: widths, gap: 12, padding: '10px 18px',
      background: T.surface2, borderBottom: `1px solid ${T.border1}`,
      fontSize: 10.5, fontWeight: 700, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.06em',
    }}>
      {cols.map((c, i) => <div key={i}>{c}</div>)}
    </div>
  );
}
